import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Addresses, CompanyAddress } from '@etoh/database/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { UserService } from 'apps/desktop/src/app/user.service';
import { omit } from 'lodash';
import {
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';
import { CompaniesService } from '../../../companies/companies.service';
import { EntitiesService } from '../../../entities/entities.service';
import { PageFormComponent } from '../../../shared/components/layout/page-form/page-form.component';
import {
  findCountryBy2LetterCode,
  getCountryOptions,
  getStateOptions,
} from '../../../shared/forms/countries.datas';
import { CommentsNotesFormAndAttachedFilesForm } from '../../../shared/forms/templates.forms';
import {
  PageFormInterface,
  PageFormMode,
} from '../../../shared/interfaces/pageForm.interface';
import { StoreService } from '../../../store.service';

@Component({
  selector: 'etoh-addresses-add',
  templateUrl: './addresses-add.component.html',
  styleUrls: ['./addresses-add.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [PageFormComponent],
})
export class AddressesAddComponent implements OnInit, PageFormInterface {
  @ViewChild(PageFormComponent, { static: false }) pageForm: PageFormComponent;
  @Input() id: number;
  @Input() standalone = true;

  fields: FormlyFieldConfig[];

  @Input() model: any = {};

  constructor(
    private storeService: StoreService,
    private companiesService: CompaniesService,
    private userService: UserService,
    private entitiesService: EntitiesService
  ) {}

  ngOnInit(): void {
    this.fields = [
      {
        expressions: { hide: '!model.companyId' },
        fieldGroupClassName: 'ant-row layout-padding',
        fieldGroup: [
          {
            key: 'companyId',
            type: 'select',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Company',
              placeholder: '',
              required: true,
              options: this.companiesService.getCompaniesForOptions$(),
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'ant-row layout-padding',
        fieldGroup: [
          {
            key: 'name',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Address code',
              placeholder: '',
              required: true,
            },
          },
          {
            key: 'storageCompany',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Storage company',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'excise',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Excise / Tax Warehouse',
              required: false,
            },
          },
          {
            key: 'bondedWarehouse',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Excise / Bonded Warehouse',
              required: false,
            },
          },
        ],
      },

      {
        fieldGroupClassName: 'ant-row layout-padding',
        fieldGroup: [
          {
            type: 'input',
            className: 'ant-col ant-col-24',
            props: {
              allowClear: true,
              type: 'address',
              label: 'Search address',
              placeholder: '',
              change: (field, value: Event) => {
                console.log('Gmaps value', value);
                this.pageForm.model = {
                  ...this.pageForm.model,
                  streetNumber: this.getStreetNumber(value),
                  street1: this.getStreet(value),
                  city: this.getCity(value),
                  state: this.getState(value),
                  country: this.getCountryShort(value),
                  zipcode: this.getPostCode(value),
                };
              },
            },
          },
          {
            key: 'streetNumber',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Street number',
              placeholder: '',
            },
          },
          {
            key: 'street1',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Street Line 1',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'street2',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Street Line 2',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'zipcode',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Zip code',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'city',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'City',
              placeholder: '',
              required: false,
            },
          },
        ],
      },
      {
        fieldGroupClassName: 'ant-row layout-padding',
        fieldGroup: [
          {
            key: 'country',
            type: 'select',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Country',
              placeholder: '',
              required: false,
              options: getCountryOptions(),
            },
          },
          {
            key: 'state',
            type: 'input',
            props: {
              label: 'State',
              placeholder: '',
              required: false,
              options: [],
            },
            hooks: {
              onInit: (field: FormlyFieldConfig) => {
                const countryControl = (field.parent as any).get(
                  'country'
                ).formControl;

                (field as any).props.options = countryControl.valueChanges.pipe(
                  startWith(countryControl.value),
                  distinctUntilChanged(),
                  map((countryId) => {
                    const states = getStateOptions(countryId as any);

                    if (!states.length) {
                      (field as any).formControl.setValue(null);
                    }

                    console.log('states', states, countryId);

                    return states;
                  })
                );
              },
            },
          },
        ],
      },
      ...CommentsNotesFormAndAttachedFilesForm,
    ];
  }

  getAddrComponent(place: any, componentTemplate: any): any {
    let result;
    for (const component of place.address_components) {
      const addressType = component.types[0];
      if (componentTemplate[addressType]) {
        result = component[componentTemplate[addressType]];
        return result;
      }
    }
  }
  getStreetNumber(place: any): any {
    const COMPONENT_TEMPLATE = { street_number: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getStreet(place: any): any {
    const COMPONENT_TEMPLATE = { route: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getCity(place: any): any {
    const COMPONENT_TEMPLATE = { locality: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getState(place: any): any {
    const COMPONENT_TEMPLATE = { administrative_area_level_1: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getDistrict(place: any): any {
    const COMPONENT_TEMPLATE = { administrative_area_level_2: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getCountryShort(place: any): any {
    const COMPONENT_TEMPLATE = { country: 'short_name' };
    const short_name = this.getAddrComponent(place, COMPONENT_TEMPLATE);
    if (!short_name) {
      return null;
    }

    return findCountryBy2LetterCode(short_name)?.value;
  }
  getCountry(place: any): any {
    const COMPONENT_TEMPLATE = { country: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getPostCode(place: any): any {
    const COMPONENT_TEMPLATE = { postal_code: 'long_name' };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }
  getPhone(place: any): any {
    const COMPONENT_TEMPLATE = {
      formatted_phone_number: 'formatted_phone_number',
    };
    return this.getAddrComponent(place, COMPONENT_TEMPLATE);
  }

  public insertFunction = (model: any) => {
    const companyId = model.companyId;

    const filteredModel = omit(model, ['companyId']);

    let res: any = null;

    return this.storeService
      .addObject<Addresses>('addresses', filteredModel)
      .pipe(
        take(1),
        switchMap((res2) => {
          res = res2;
          if (companyId) {
            return this.storeService.addObject<CompanyAddress>(
              'companyAddress',
              {
                companyId,
                addressId: res.returning?.[0].id,
                createdBy: this.userService.userId$.value,
              }
            );
          }

          return of(null);
        }),
        map(() => res)
      );
  };

  // Check if name is duplicated
  shouldPrevent = (model: any, mode: PageFormMode) => {
    if (mode === 'edit') {
      return of(false);
    }
    return this.entitiesService.getCorrespondanceAndShowDialog<Addresses>(
      'addresses',
      ['name'],
      model?.name
    );
  };
}
